import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { uploadFile, createUploadController } from "@/lib/upload";
import { typesForVault, TYPE_CONFIG, TODO_STATUSES } from "@/lib/config";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Upload, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const empty = (vault) => ({
  vault, type: "", title: "", department: "", category: "", owner: "",
  status: vault === "todos" ? "new" : "active", expiration_date: "", notes: "", tags: [], fields: {},
  file_id: null, related_ids: [], folder_id: "",
});

const NONE = "__none__";

const buildFolderOptions = (folders) => {
  const byParent = {};
  folders.forEach((f) => { (byParent[f.parent_id || ""] = byParent[f.parent_id || ""] || []).push(f); });
  const out = [];
  const walk = (pid, depth) => {
    (byParent[pid] || []).sort((a, b) => a.name.localeCompare(b.name)).forEach((f) => {
      out.push({ id: f.id, name: f.name, depth });
      walk(f.id, depth + 1);
    });
  };
  walk("", 0);
  return out;
};

export default function ItemFormDialog({ open, onClose, vault, item, onSaved, folders = [], defaultFolderId = "", spaceId = "" }) {
  const [form, setForm] = useState(empty(vault));
  const [tagsText, setTagsText] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const uploadCtrlRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({ ...empty(vault), ...item, folder_id: item.folder_id || "", expiration_date: item.expiration_date || "" });
      setTagsText((item.tags || []).join(", "));
      setFileName(item.file_id ? "Attached file" : "");
    } else {
      setForm({ ...empty(vault), folder_id: defaultFolderId || "" });
      setTagsText("");
      setFileName("");
    }
  }, [item, vault, open, defaultFolderId]);

  const folderOptions = buildFolderOptions(folders);

  const typeCfg = form.type ? TYPE_CONFIG[form.type] : null;
  const types = typesForVault(vault);

  const setField = (k, v) => setForm((f) => ({ ...f, fields: { ...f.fields, [k]: v } }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024 * 1024) return toast.error("File exceeds 2GB limit");
    const ctrl = createUploadController();
    uploadCtrlRef.current = ctrl;
    setUploading(true);
    setUploadPct(0);
    try {
      const data = await uploadFile(file, { controller: ctrl, onProgress: (p) => setUploadPct(Math.round(p * 100)) });
      setForm((f) => ({ ...f, file_id: data.id }));
      setFileName(file.name);
      toast.success("File uploaded");
    } catch (err) {
      if (ctrl.signal.aborted) toast("Upload cancelled");
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      uploadCtrlRef.current = null;
      setUploading(false);
      setUploadPct(null);
    }
  };

  const save = async () => {
    if (!form.type) return toast.error("Select an asset type");
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = {
      ...form,
      space_id: item?.space_id || spaceId,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      expiration_date: form.expiration_date || null,
    };
    try {
      if (item?.id) await api.put(`/items/${item.id}`, payload);
      else await api.post("/items", payload);
      toast.success(item ? "Updated" : "Created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto vault-scroll" data-testid="item-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? "Edit asset" : "New asset"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Asset type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))} disabled={!!item}>
              <SelectTrigger data-testid="type-select"><SelectValue placeholder="Choose type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={form.title} data-testid="title-input"
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. renovaglobalsystem.com" />
          </div>

          <div className="space-y-1.5">
            <Label>Folder</Label>
            <Select value={form.folder_id || NONE} onValueChange={(v) => setForm((f) => ({ ...f, folder_id: v === NONE ? "" : v }))}>
              <SelectTrigger data-testid="folder-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Unfiled</SelectItem>
                {folderOptions.map((fo) => (
                  <SelectItem key={fo.id} value={fo.id} textValue={fo.name}>
                    {"\u00A0\u00A0".repeat(fo.depth)}{fo.depth > 0 ? "↳ " : ""}{fo.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Input value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} placeholder="Technology" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="Infrastructure" />
            </div>
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Input value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} placeholder="Responsible person" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {vault === "todos"
                    ? TODO_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)
                    : ["active", "inactive", "expired", "pending", "archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {typeCfg && typeCfg.fields.length > 0 && (
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">{typeCfg.label} details</div>
              {typeCfg.fields.map((fd) => (
                <div key={fd.key} className="space-y-1.5">
                  <Label>{fd.label}</Label>
                  {fd.type === "select" ? (
                    <Select value={form.fields[fd.key] || ""} onValueChange={(v) => setField(fd.key, v)}>
                      <SelectTrigger data-testid={`field-${fd.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{fd.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : fd.type === "password" ? (
                    <Input type="password" value={form.fields[fd.key] || ""} data-testid={`field-${fd.key}`}
                      onChange={(e) => setField(fd.key, e.target.value)} placeholder="Encrypted at rest" />
                  ) : (
                    <Input type={fd.type === "date" ? "date" : "text"} value={form.fields[fd.key] || ""} data-testid={`field-${fd.key}`}
                      onChange={(e) => setField(fd.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          )}

          {typeCfg?.file && (
            <div className="space-y-1.5">
              <Label>File</Label>
              <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-md px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-gray-500" />}
                <span className="text-sm text-gray-600">
                  {uploading && uploadPct !== null ? `Uploading… ${uploadPct}%` : (fileName || "Click to upload (max 2GB)")}
                </span>
                {uploading && (
                  <button type="button" onClick={(e) => { e.preventDefault(); uploadCtrlRef.current?.cancel(); }}
                    className="ml-auto text-xs text-red-500 hover:underline" data-testid="cancel-upload-btn">Cancel</button>
                )}
                <input type="file" className="hidden" onChange={handleFile} data-testid="file-input" />
              </label>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Expiration date</Label>
            <Input type="date" value={form.expiration_date || ""} data-testid="expiration-input"
              onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <Label>Tags (comma separated)</Label>
            <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="production, critical" />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} />
          </div>

          <div className="flex items-center gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
            <Lock className="w-4 h-4 text-gray-500 shrink-0" />
            <div className="text-[11px] text-gray-500">
              New items are <span className="font-medium text-gray-700">private</span> — only you and admins can see them. After saving, use <span className="font-medium text-gray-700">Access</span> on the item to grant View / Modify to specific people or require a passkey to open.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-item-btn" className="bg-[#0A0A0A] hover:bg-gray-800">
            {saving ? "Saving…" : "Save asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
